import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import type { SerializedEditorState } from "lexical";
import { type FC, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { FormStack } from "../../generic/components/form-fields/form-stack";

export const FooterSettingsEditor: FC = () => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<EmailDesignSchema>();

  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

  const handleToggleFooter = useCallback(
    (checked: boolean): void => {
      if (checked) {
        const emptyFooter: SerializedEditorState = {
          root: {
            type: "root",
            version: 1,
            children: [
              {
                type: "paragraph",
                version: 0,
              },
            ],
            direction: "ltr",
            format: "left",
            indent: 0,
          },
        };
        setValue("footer", emptyFooter);
      } else {
        setOpenWarningModal(true);
      }
    },
    [setValue],
  );

  return (
    <>
      {openWarningModal && (
        <WarningModal
          open={openWarningModal}
          closeModal={() => setOpenWarningModal(false)}
          onContinue={() => {
            setValue("footer", null);
            setOpenWarningModal(false);
          }}
        />
      )}
      <FormStack>
        <CeToggle<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer"
          label={t("Enable Footer")}
          onChange={handleToggleFooter}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_side"
          label={t("Side Padding")}
          min={0}
          max={50}
          step={1}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_top"
          label={t("Top Padding")}
          min={0}
          max={50}
          step={1}
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="footer_layout_settings.paper_padding_bottom"
          label={t("Bottom Padding")}
          min={0}
          max={50}
          step={1}
        />
      </FormStack>
    </>
  );
};

const WarningModal: FC<{ open: boolean; closeModal: () => void; onContinue: () => void }> = ({
  open,
  closeModal,
  onContinue,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={closeModal}>
      <DialogTitle>{t("Remove Footer?")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Trans>Disabling the footer will delete its contents. Are you sure?</Trans>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="email-design-remove-footer-dialog-cancel"
          variant="outlined"
          color="secondary"
          onClick={closeModal}
        >
          {t("Cancel")}
        </Button>
        <Button
          data-analytics-id="email-design-remove-footer-dialog-continue"
          variant="outlined"
          onClick={onContinue}
        >
          {t("Continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
